import { X } from "lucide-react";

import { cn } from "../lib/utils";
import { getPublicMaintenanceBannerMessage } from "../config/public-runtime-config";
import { useMaintenanceBanner, useMaintenanceBannerVisible } from "../hooks/use-maintenance-banner";

/** Approximate rendered height — keep layout offsets in sync. */
export const MAINTENANCE_BANNER_HEIGHT = "2.25rem";

type MaintenanceBannerProps = {
  className?: string;
  /** Pin above fixed sidebars so the banner spans the full viewport width. */
  fixed?: boolean;
};

export const MaintenanceBanner = ({ className, fixed = false }: MaintenanceBannerProps) => {
  const message = getPublicMaintenanceBannerMessage();
  const visible = useMaintenanceBannerVisible();
  const dismiss = useMaintenanceBanner(state => state.dismiss);

  if (!visible || !message) {
    return null;
  }

  const handleDismiss = () => {
    dismiss(message);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "w-full bg-amber-100 text-amber-950 dark:bg-amber-950/80 dark:text-amber-100 border-b border-amber-300/70 dark:border-amber-800 px-4 py-2 text-sm font-medium",
        fixed && "fixed top-0 left-0 right-0 z-30",
        className
      )}
    >
      <div className="flex items-center gap-2">
        <p className="flex-1 text-center">{message}</p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss maintenance notice"
          className="shrink-0 rounded p-1 text-amber-950/80 hover:bg-amber-200/80 hover:text-amber-950 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-700 dark:text-amber-100/80 dark:hover:bg-amber-900 dark:hover:text-amber-50"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
};

export { useMaintenanceBannerVisible };

export const useMaintenanceBannerOffset = (): string => {
  const visible = useMaintenanceBannerVisible();
  return visible ? MAINTENANCE_BANNER_HEIGHT : "0px";
};
